import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { CreateTeamDto, TeamForm } from '../../models/team.model';
import { AppForm } from '../../../apps/models/app.model';

import { FormInputComponent } from '../../../../shared/forms/form-input/form-input.component';
import { FormTitleComponent } from '../../../../shared/forms/form-title/form-title.component';
import { ApplicationsService } from '../../../apps/services/apps.service';
import { ToasterService } from '../../../../shared/toasts/services/toasts.service';
import { ModalFormComponent } from '../../../../shared/forms/modal-form/modal-form.component';

@Component({
  selector: 'app-team-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormInputComponent,
    FormTitleComponent,
    ModalFormComponent,
  ],
  templateUrl: './team-form.component.html',
  styleUrls: ['./team-form.component.scss', '../../../../shared/forms/form-layout.scss'],
})
export class TeamFormComponent implements OnInit {
  @Input() options: AppForm[] | null = null;
  @Input() initialData: TeamForm | null = null;

  // ✅ CORREGIDO: ahora emite DTO
  @Output() formSubmit = new EventEmitter<CreateTeamDto>();
  @Output() formClosed = new EventEmitter<void>();
  @Output() formValidChange = new EventEmitter<boolean>();

  private readonly fb = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);
  private readonly applicationsService = inject(ApplicationsService);

  form!: FormGroup;

  isLoading = false;
  isModalReady = false;
  private initialFormValue: unknown = null;
  hasChanges = false;

  appOptions: { id: string; name: string }[] = [];

  // ✅ GETTERS TIPADOS
  get groupNameControl(): FormControl {
    return this.form.get('groupName') as FormControl;
  }

  get priorityControl(): FormControl {
    return this.form.get('priority') as FormControl;
  }

  get appIdControl(): FormControl {
    return this.form.get('appId') as FormControl;
  }

  ngOnInit(): void {
    const data = this.initialData ?? ({} as TeamForm);

    // ✅ opciones desde padre o API
    if (this.options && this.options.length > 0) {
      this.appOptions = this.options.map((a) => ({
        id: String(a.appId ?? ''),
        name: a.appName,
      }));
    } else {
      this.loadApplications();
    }

    // ✅ normalizar appId
    const initialAppId = this.resolveInitialAppId(data);

    // ✅ formulario
    this.form = this.fb.group({
      groupName: [data.groupName ?? '', Validators.required],
      priority: [data.priority ?? '', [Validators.required, Validators.min(1)]],
      appId: [initialAppId, Validators.required],
    });

    // En modo edición, remover required de campos que no tienen valor en el control
    if (this.initialData) {
      // Verificar el valor actual del control del formulario
      const groupNameValue = this.groupNameControl.value as string | null | undefined;
      const priorityValue = this.priorityControl.value as string | number | null | undefined;

      const hasGroupName = groupNameValue !== null && groupNameValue !== undefined && groupNameValue !== '';
      const hasPriority = priorityValue !== null && priorityValue !== undefined && priorityValue !== '' && priorityValue !== 0;

      if (!hasGroupName) {
        this.groupNameControl.clearValidators();
        this.groupNameControl.updateValueAndValidity();
      }
      if (!hasPriority) {
        this.priorityControl.clearValidators();
        this.priorityControl.setValidators([Validators.min(1)]);
        this.priorityControl.updateValueAndValidity();
      }
    }

    // ✅ detectar cambios
    if (this.initialData) {
      this.initialFormValue = this.form.value;
      this.hasChanges = false;

      this.form.valueChanges.subscribe(() => {
        this.hasChanges =
          JSON.stringify(this.form.value) !==
          JSON.stringify(this.initialFormValue);
      });
    } else {
      this.hasChanges = true;
    }

    // ✅ estado formulario
    this.form.statusChanges.subscribe(() => {
      this.formValidChange.emit(this.form.valid);
    });

    this.formValidChange.emit(this.form.valid);

    // ✅ mostrar modal
    setTimeout(() => {
      this.isModalReady = true;
    }, 50);
  }

  // ✅ cargar aplicaciones
  loadApplications() {
    this.applicationsService.getApplications().subscribe({
      next: (res: AppForm[]) => {
        this.appOptions = res.map((app) => ({
          id: String(app.appId),
          name: app.appName,
        }));
      },
      error: () => {
        this.toaster.error('Error', 'No se pudo cargar las aplicaciones');
      },
    });
  }

  onSubmit(): void {
    if (this.form.invalid || this.isLoading) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    let appIdValue = this.form.value.appId;

    // ✅ normalización robusta
    if (Array.isArray(appIdValue)) {
      appIdValue = appIdValue[0];
    }

    if (appIdValue && typeof appIdValue === 'object') {
      appIdValue = appIdValue.id;
    }

    const teamData: CreateTeamDto = {
      groupName: String(this.form.value.groupName),
      priority: Number(this.form.value.priority),
      appId: String(appIdValue),
    };

    this.formSubmit.emit(teamData);
  }

  cancelar(): void {
    this.formClosed.emit();
  }

  private resolveInitialAppId(data: TeamForm): string {
    const appIdVal = data['appId'] as unknown;
    const applicationVal = data['application'] as unknown;
    const raw: unknown = appIdVal ?? applicationVal ?? '';

    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const o = raw as Record<string, unknown>;
      const id = o['id'] ?? o['appId'] ?? o['value'];
      if (typeof id === 'string' || typeof id === 'number') {
        return String(id);
      }
      return '';
    }

    if (typeof raw === 'string' || typeof raw === 'number') {
      return String(raw);
    }

    return '';
  }
}
